import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  collection, 
  getDocs, 
  addDoc,
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import "./styles.css";
import "./auth-management.css";

export default function TeamManagement() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    leaderId: "",
  });

  // Fetch teams and users
  const fetchData = async () => {
    try {
      // Fetch teams
      const teamsQuery = query(collection(db, "teams"), orderBy("createdAt", "desc"));
      const teamsSnapshot = await getDocs(teamsQuery);
      const teamsData = teamsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeams(teamsData);

      // Fetch users
      const usersSnapshot = await getDocs(collection(db, "users"));
      const usersData = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setStatusMessage({ type: "error", text: "Failed to load data" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get team members
  const getTeamMembers = (teamId) => {
    return users.filter(user => user.teamId === teamId);
  };

  // Get available users (not in any team)
  const getAvailableUsers = () => {
    return users.filter(user => !user.teamId);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Create new team
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    
    try {
      const newTeam = {
        name: formData.name,
        description: formData.description,
        leaderId: formData.leaderId || null,
        members: formData.leaderId ? [formData.leaderId] : [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userProfile?.id || null,
      };

      const docRef = await addDoc(collection(db, "teams"), newTeam);

      // If leader assigned, update their teamId
      if (formData.leaderId) {
        await updateDoc(doc(db, "users", formData.leaderId), {
          teamId: docRef.id,
          teamName: formData.name,
          updatedAt: serverTimestamp()
        });
      }

      setStatusMessage({ type: "success", text: "Team created successfully" });
      setIsModalOpen(false);
      setFormData({ name: "", description: "", leaderId: "" });
      fetchData();
    } catch (error) {
      console.error("Error creating team:", error);
      setStatusMessage({ type: "error", text: "Failed to create team" });
    }
  };

  // Update team
  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    if (!editingTeam) return;

    try {
      const teamRef = doc(db, "teams", editingTeam.id);
      await updateDoc(teamRef, {
        name: formData.name,
        description: formData.description,
        leaderId: formData.leaderId || null,
        updatedAt: serverTimestamp()
      });

      setStatusMessage({ type: "success", text: "Team updated successfully" });
      setEditingTeam(null);
      setFormData({ name: "", description: "", leaderId: "" });
      fetchData();
    } catch (error) {
      console.error("Error updating team:", error);
      setStatusMessage({ type: "error", text: "Failed to update team" });
    }
  };

  // Delete team
  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm("Are you sure you want to delete this team? Members will be unassigned.")) {
      return;
    }

    try {
      // Remove team reference from all members
      const members = getTeamMembers(teamId);
      for (const member of members) {
        await updateDoc(doc(db, "users", member.id), {
          teamId: null,
          teamName: null,
          updatedAt: serverTimestamp()
        });
      }

      // Delete the team
      await deleteDoc(doc(db, "teams", teamId));
      
      setStatusMessage({ type: "success", text: "Team deleted successfully" });
      setSelectedTeam(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting team:", error);
      setStatusMessage({ type: "error", text: "Failed to delete team" });
    }
  };

  // Add member to team
  const handleAddMember = async (teamId, userId) => {
    try {
      const team = teams.find(t => t.id === teamId);
      if (!team) return;

      // Update user's team reference
      await updateDoc(doc(db, "users", userId), {
        teamId: teamId,
        teamName: team.name,
        updatedAt: serverTimestamp()
      });

      // Update team's members array
      const updatedMembers = [...(team.members || []), userId];
      await updateDoc(doc(db, "teams", teamId), {
        members: updatedMembers,
        updatedAt: serverTimestamp()
      });

      setStatusMessage({ type: "success", text: "Member added successfully" });
      fetchData();
    } catch (error) {
      console.error("Error adding member:", error);
      setStatusMessage({ type: "error", text: "Failed to add member" });
    }
  };

  // Remove member from team
  const handleRemoveMember = async (teamId, userId) => {
    try {
      const team = teams.find(t => t.id === teamId);
      if (!team) return;

      // Update user's team reference
      await updateDoc(doc(db, "users", userId), {
        teamId: null,
        teamName: null,
        updatedAt: serverTimestamp()
      });

      // Update team's members array
      const updatedMembers = (team.members || []).filter(id => id !== userId);
      await updateDoc(doc(db, "teams", teamId), {
        members: updatedMembers,
        updatedAt: serverTimestamp()
      });

      setStatusMessage({ type: "success", text: "Member removed successfully" });
      fetchData();
    } catch (error) {
      console.error("Error removing member:", error);
      setStatusMessage({ type: "error", text: "Failed to remove member" });
    }
  };

  // Open edit modal
  const openEditModal = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      description: team.description || "",
      leaderId: team.leaderId || "",
    });
  };

  // Check if current user has manager/admin access
  const canManage = userProfile?.role === "admin" || userProfile?.role === "manager";

  if (loading) {
    return (
      <div className="management-container">
        <div className="loading-spinner">Loading teams...</div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <div className="management-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
        <h1>👥 Team Management</h1>
        <p>Create and manage your teams</p>
      </div>

      {statusMessage.text && (
        <div className={`status-message status-${statusMessage.type}`}>
          {statusMessage.type === "success" ? "✅" : "❌"} {statusMessage.text}
        </div>
      )}

      <div className="management-toolbar">
        <div className="team-count">
          {teams.length} team{teams.length !== 1 ? "s" : ""}
        </div>
        {canManage && (
          <button className="new-btn" onClick={() => setIsModalOpen(true)}>
            + Create Team
          </button>
        )}
      </div>

      <div className="teams-grid">
        {teams.map((team) => {
          const members = getTeamMembers(team.id);
          const leader = users.find(u => u.id === team.leaderId);
          
          return (
            <div 
              key={team.id} 
              className={`team-card ${selectedTeam?.id === team.id ? "selected" : ""}`}
              onClick={() => setSelectedTeam(team)}
            >
              <div className="team-card-header">
                <h3>{team.name}</h3>
                {canManage && (
                  <div className="team-actions">
                    <button 
                      className="action-btn edit-btn"
                      onClick={(e) => { e.stopPropagation(); openEditModal(team); }}
                      title="Edit team"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team.id); }}
                      title="Delete team"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
              
              <p className="team-description">{team.description || "No description"}</p>
              
              <div className="team-meta">
                <div className="team-leader">
                  <strong>👑 Leader:</strong> {leader?.displayName || "Not assigned"}
                </div>
                <div className="team-member-count">
                  <strong>👥 Members:</strong> {members.length}
                </div>
              </div>

              <div className="team-members-preview">
                {members.slice(0, 5).map((member) => (
                  <div key={member.id} className="member-avatar" title={member.displayName}>
                    {member.displayName?.charAt(0).toUpperCase() || "?"}
                  </div>
                ))}
                {members.length > 5 && (
                  <div className="member-avatar more">+{members.length - 5}</div>
                )}
              </div>
            </div>
          );
        })}
        
        {teams.length === 0 && (
          <div className="empty-state-centered">
            <div className="empty-icon">👥</div>
            <p>No teams created yet</p>
            {canManage && (
              <button className="new-btn" onClick={() => setIsModalOpen(true)}>
                Create Your First Team
              </button>
            )}
          </div>
        )}
      </div>

      {/* Team Details Panel */}
      {selectedTeam && (
        <div className="team-details-panel">
          <div className="panel-header">
            <h2>{selectedTeam.name}</h2>
            <button className="close-btn" onClick={() => setSelectedTeam(null)}>×</button>
          </div>
          
          <div className="panel-content">
            <h3>Team Members</h3>
            
            <div className="members-list">
              {getTeamMembers(selectedTeam.id).map((member) => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar-large">
                      {member.displayName?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="member-name">
                        {member.displayName}
                        {member.id === selectedTeam.leaderId && (
                          <span className="leader-badge">👑 Leader</span>
                        )}
                      </div>
                      <div className="member-email">{member.email}</div>
                      <div className="member-role">{member.role}</div>
                    </div>
                  </div>
                  {canManage && member.id !== selectedTeam.leaderId && (
                    <button 
                      className="remove-member-btn"
                      onClick={() => handleRemoveMember(selectedTeam.id, member.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              
              {getTeamMembers(selectedTeam.id).length === 0 && (
                <p className="no-members">No members in this team</p>
              )}
            </div>

            {canManage && getAvailableUsers().length > 0 && (
              <div className="add-member-section">
                <h4>Add Member</h4>
                <div className="available-users">
                  {getAvailableUsers().map((user) => (
                    <div key={user.id} className="available-user-item">
                      <span>{user.displayName} ({user.email})</span>
                      <button 
                        className="add-member-btn"
                        onClick={() => handleAddMember(selectedTeam.id, user.id)}
                      >
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Team Modal */}
      {(isModalOpen || editingTeam) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingTeam ? "Edit Team" : "Create New Team"}</h2>
            <form onSubmit={editingTeam ? handleUpdateTeam : handleCreateTeam}>
              <div className="form-group">
                <label>Team Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Engineering Team"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Brief description of the team..."
                />
              </div>
              
              <div className="form-group">
                <label>Team Leader</label>
                <select
                  name="leaderId"
                  value={formData.leaderId}
                  onChange={handleChange}
                >
                  <option value="">Select a leader</option>
                  {users.filter(u => u.role === "manager" || u.role === "admin").map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingTeam(null);
                    setFormData({ name: "", description: "", leaderId: "" });
                  }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button type="submit" className="new-btn" style={{ flex: 1 }}>
                  {editingTeam ? "Save Changes" : "Create Team"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
